import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { PageHeader, Kpi, Section, StatusBadge, fmtTime } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function EmpDashboard() {
  const [today, setToday] = useState({});
  const [settings, setSettings] = useState({ daily_hours: 8 });
  const [breakMin, setBreakMin] = useState(15);
  const [balance, setBalance] = useState([]);
  const [myTS, setMyTS] = useState([]);
  const [rejected, setRejected] = useState([]);

  const load = async () => {
    try {
      const [t, s, b, ts, wk] = await Promise.all([
        api.get("/attendance/today"),
        api.get("/settings"),
        api.get("/leave/balance"),
        api.get("/timesheets"),
        api.get("/timesheets/weeks?status=rejected"),
      ]);
      setToday(t.data || {}); setSettings(s.data); setBalance(b.data);
      const sorted = (ts.data || []).slice().sort((a, b) => (a.date < b.date ? 1 : -1));
      setMyTS(sorted.slice(0, 5));
      setRejected(wk.data || []);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const clockIn = async () => {
    try { await api.post("/attendance/clock-in"); toast.success("Clocked in"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const clockOut = async () => {
    try { await api.post("/attendance/clock-out"); toast.success("Clocked out"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const addBreak = async () => {
    try { await api.post(`/attendance/break?minutes=${breakMin}`); toast.success(`+${breakMin}m break`); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  // Compute live hours
  const ci = today.clock_in ? new Date(today.clock_in) : null;
  const co = today.clock_out ? new Date(today.clock_out) : null;
  const end = co || new Date();
  const workedMin = ci ? Math.max(0, (end - ci) / 60000 - (today.break_minutes || 0)) : 0;
  const workedH = (workedMin / 60).toFixed(2);
  const required = settings.daily_hours || 8;
  const remain = Math.max(0, required - workedMin / 60).toFixed(2);

  return (
    <div data-testid="emp-dashboard">
      <PageHeader overline="Today" title={new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        subtitle="Clock in to start your day and log hours against your assigned projects." />

      {rejected.length > 0 && (
        <div className="mb-6 border border-red-300 bg-red-50 p-4" data-testid="rejected-timesheets-banner">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="eyebrow text-red-700">Action required</div>
              <div className="font-head text-lg font-semibold text-red-800">
                {rejected.length} week{rejected.length === 1 ? "" : "s"} returned for revision
              </div>
              <div className="text-xs text-red-700 mt-1">Click a week to open, fix the entries, and resubmit.</div>
            </div>
            <div className="flex flex-wrap gap-2 max-w-full">
              {rejected.slice(0, 5).map((r) => (
                <Link key={r.id} to={`/timesheet?date=${r.week_start}`}
                  data-testid={`rejected-week-link-${r.week_start}`}
                  className="inline-flex items-center gap-2 px-3 h-9 border border-red-400 bg-white text-red-700 hover:bg-red-600 hover:text-white transition-colors text-xs font-semibold">
                  Week of {r.week_start} <span className="opacity-70">· {r.total_hours}h →</span>
                </Link>
              ))}
              {rejected.length > 5 && (
                <span className="text-xs text-red-700 self-center">+{rejected.length - 5} more</span>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 bg-white border border-[#E5E5E5] p-6 lg:p-8">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div>
              <div className="eyebrow">Current Session</div>
              <div className="flex items-baseline gap-6 mt-4">
                <div>
                  <div className="text-xs text-neutral-500">Clock In</div>
                  <div className="font-head text-2xl font-semibold">{fmtTime(today.clock_in)}</div>
                </div>
                <div>
                  <div className="text-xs text-neutral-500">Clock Out</div>
                  <div className="font-head text-2xl font-semibold">{fmtTime(today.clock_out)}</div>
                </div>
                <div>
                  <div className="text-xs text-neutral-500">Break</div>
                  <div className="font-head text-2xl font-semibold">{today.break_minutes || 0}m</div>
                </div>
              </div>
              <div className="mt-6">
                <div className="eyebrow">Worked / Required</div>
                <div className="font-head text-5xl lg:text-6xl font-light tracking-tighter mt-2">
                  <span className="text-[#FF5500]">{workedH}</span>
                  <span className="text-neutral-400 text-3xl"> / {required}h</span>
                </div>
                <div className="text-sm text-neutral-500 mt-1">Remaining: {remain}h</div>
              </div>
            </div>
            <div className="flex flex-col gap-3 min-w-[200px]">
              {!today.clock_in ? (
                <Button data-testid="clock-in-button" onClick={clockIn}
                  className="rounded-full h-14 px-8 bg-[#FF5500] hover:bg-[#E64D00] text-white font-semibold text-base">
                  ▶ Clock In
                </Button>
              ) : !today.clock_out ? (
                <Button data-testid="clock-out-button" onClick={clockOut}
                  className="rounded-full h-14 px-8 bg-[#1F4E8C] hover:bg-[#163a68] text-white font-semibold text-base">
                  ■ Clock Out
                </Button>
              ) : (
                <div className="text-center p-4 border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-medium">Day Complete ✓</div>
              )}
              {today.clock_in && !today.clock_out && (
                <div className="flex gap-2">
                  <Input type="number" value={breakMin} onChange={(e) => setBreakMin(+e.target.value || 0)}
                    className="rounded-none border-[#E5E5E5] h-10" data-testid="break-minutes" />
                  <Button variant="outline" onClick={addBreak} data-testid="add-break"
                    className="rounded-none border-[#1F4E8C] text-[#1F4E8C] h-10">+ Break</Button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E5E5E5] p-6">
          <div className="eyebrow mb-4">Leave Balance</div>
          <div className="space-y-3">
            {balance.map((b) => (
              <div key={b.type} className="flex items-center justify-between pb-2 border-b border-[#F0F0EE] last:border-0">
                <div>
                  <div className="text-sm font-medium">{b.type}</div>
                  <div className="text-xs text-neutral-500">Used {b.used} of {b.allocated}</div>
                </div>
                <div className="font-head text-2xl font-semibold text-[#FF5500]">{b.remaining}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Section title="Recent Timesheets">
        <table className="w-full table-dense">
          <thead>
            <tr className="text-left border-b border-[#E5E5E5]">
              <th className="eyebrow">Date</th><th className="eyebrow">Hours</th><th className="eyebrow">Billable</th><th className="eyebrow">Status</th>
            </tr>
          </thead>
          <tbody>
            {myTS.length === 0 && <tr><td colSpan={4} className="text-center text-neutral-500 py-8">No timesheets yet. Head to My Timesheet to log hours.</td></tr>}
            {myTS.map((t) => (
              <tr key={t.id} className="border-b border-[#F0F0EE]">
                <td>{t.date}</td>
                <td>{t.total_hours}</td>
                <td>{t.billable_hours}</td>
                <td><StatusBadge status={t.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    </div>
  );
}
